import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import api from '../services/api';

const BusinessInvoiceForm = ({ projectMode = false }) => {
  // Contexte du projet si en mode projet
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  
  // Base URL de l'API et routes selon le mode
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  const routeBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/workspace';
  
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [showClientModal, setShowClientModal] = useState(false);
  
  // État pour l'autocomplétion des produits
  const [activeProductDropdown, setActiveProductDropdown] = useState(null);
  const [productSearches, setProductSearches] = useState({});
  const dropdownRefs = useRef({});
  
  const [form, setForm] = useState({
    client_id: '',
    type: 'standard',
    date_facture: new Date().toISOString().split('T')[0],
    date_echeance: '',
    notes: '',
    conditions: ''
  });
  
  const [items, setItems] = useState([
    { description: '', quantite: 1, prix_unitaire: '', product_id: '' }
  ]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, projectId]);
  
  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeProductDropdown !== null) {
        const ref = dropdownRefs.current[activeProductDropdown];
        if (ref && !ref.contains(event.target)) {
          setActiveProductDropdown(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeProductDropdown]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [clientsRes, productsRes] = await Promise.all([
        api.get(`${apiBase}/clients`),
        api.get(`${apiBase}/products`)
      ]);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);

      if (id) {
        const res = await api.get(`${apiBase}/invoices/${id}`);
        if (res.data) {
          setForm({
            client_id: res.data.client_id || '',
            type: res.data.type || 'standard',
            date_facture: res.data.date_facture?.split(' ')[0] || new Date().toISOString().split('T')[0],
            date_echeance: res.data.date_echeance?.split(' ')[0] || '',
            notes: res.data.notes || '',
            conditions: res.data.conditions || ''
          });
          if (res.data.items && res.data.items.length > 0) {
            setItems(res.data.items.map(item => ({
              id: item.id,
              description: item.description || '',
              quantite: item.quantite || 1,
              prix_unitaire: item.prix_unitaire || '',
              product_id: item.product_id || ''
            })));
          }
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setItems([...items, { description: '', quantite: 1, prix_unitaire: '', product_id: '' }]);
    setProductSearches(prev => ({ ...prev, [items.length]: '' }));
  };

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
      const newSearches = { ...productSearches };
      delete newSearches[index];
      setProductSearches(newSearches);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };
  
  const handleProductSelect = (index, product) => {
    const newItems = [...items];
    newItems[index].product_id = product.id;
    newItems[index].description = product.nom;
    newItems[index].prix_unitaire = product.prix_vente;
    setItems(newItems);
    setProductSearches(prev => ({ ...prev, [index]: product.nom }));
    setActiveProductDropdown(null);
  };
  
  const handleProductSearchChange = (index, value) => {
    setProductSearches(prev => ({ ...prev, [index]: value }));
    setActiveProductDropdown(index);
    
    // Si on efface, réinitialiser le produit sélectionné
    if (value === '') {
      const newItems = [...items];
      newItems[index].product_id = '';
      setItems(newItems);
    }
  };
  
  const getFilteredProducts = (index) => {
    const search = (productSearches[index] || '').toLowerCase();
    if (!search) return products.slice(0, 10);
    return products.filter(p => 
      p.nom?.toLowerCase().includes(search) ||
      p.reference?.toLowerCase().includes(search) ||
      p.categorie?.toLowerCase().includes(search)
    ).slice(0, 10);
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => {
      return sum + (parseFloat(item.quantite) || 0) * (parseFloat(item.prix_unitaire) || 0);
    }, 0);
  };

  const handleSubmit = async (e, action = 'save') => {
    e.preventDefault();
    
    // Validation
    if (!form.client_id) {
      alert('Veuillez sélectionner un client');
      return;
    }
    
    const validItems = items.filter(item => item.description && item.prix_unitaire);
    if (validItems.length === 0) {
      alert('Ajoutez au moins une ligne de facture');
      return;
    }

    // Calculer le total de chaque item et les totaux globaux
    const itemsWithTotal = validItems.map(item => ({
      ...item,
      quantite: parseFloat(item.quantite) || 1,
      prix_unitaire: parseFloat(item.prix_unitaire) || 0,
      total: (parseFloat(item.quantite) || 1) * (parseFloat(item.prix_unitaire) || 0)
    }));
    
    const sousTotal = itemsWithTotal.reduce((sum, item) => sum + item.total, 0);

    setSaving(true);
    try {
      const data = {
        ...form,
        items: itemsWithTotal,
        sous_total: sousTotal,
        total: sousTotal,
        statut: action === 'send' ? 'envoyee' : 'brouillon'
      };

      if (id) {
        await api.put(`${apiBase}/invoices/${id}`, data);
      } else {
        await api.post(`${apiBase}/invoices`, data);
      }
      
      navigate(`${routeBase}/invoices`);
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const subtotal = calculateSubtotal();
  const selectedClient = clients.find(c => String(c.id) === String(form.client_id));

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">

        <h1 className="text-2xl font-bold text-gray-800">
          {id ? 'Modifier la facture' : 'Nouvelle facture'}
        </h1>
      </div>

      <form onSubmit={(e) => handleSubmit(e, 'save')}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Selection */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-800 mb-4">Client</h2>
              
              <div className="flex gap-4">
                <select
                  value={form.client_id}
                  onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  className="flex-1 border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                >
                  <option value="">Sélectionner un client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.nom} {client.credit_balance > 0 ? `(Crédit: ${parseFloat(client.credit_balance).toLocaleString()} XOF)` : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowClientModal(true)}
                  className="px-4 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition"
                >
                  + Nouveau
                </button>
              </div>
              
              {selectedClient && (
                <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                  <div className="text-sm text-gray-600">
                    <div className="font-medium text-gray-800">{selectedClient.nom}</div>
                    {selectedClient.telephone && <div>📱 {selectedClient.telephone}</div>}
                    {selectedClient.email && <div>📧 {selectedClient.email}</div>}
                    {selectedClient.adresse && <div>📍 {selectedClient.adresse}</div>}
                    {selectedClient.credit_balance > 0 && (
                      <div className="mt-2 text-green-600 font-medium">
                        💰 Crédit disponible: {parseFloat(selectedClient.credit_balance).toLocaleString()} XOF
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Invoice Details */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-800 mb-4">Détails facture</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="standard">Facture standard</option>
                    <option value="proforma">Facture proforma</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date facture</label>
                  <input
                    type="date"
                    value={form.date_facture}
                    onChange={(e) => setForm({ ...form, date_facture: e.target.value })}
                    className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date d'échéance</label>
                  <input
                    type="date"
                    value={form.date_echeance}
                    onChange={(e) => setForm({ ...form, date_echeance: e.target.value })}
                    className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Invoice Items */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-800 mb-4">Articles / Services</h2>
              
              <div className="space-y-4">
                {items.map((item, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-xl">
                    {/* Ligne 1: Produit et Description */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div className="relative" ref={el => dropdownRefs.current[index] = el}>
                        <label className="block text-xs text-gray-500 mb-1">Produit (optionnel)</label>
                        <input
                          type="text"
                          value={productSearches[index] ?? (item.product_id ? products.find(p => String(p.id) === String(item.product_id))?.nom : '') ?? ''}
                          onChange={(e) => handleProductSearchChange(index, e.target.value)}
                          onFocus={() => setActiveProductDropdown(index)}
                          placeholder="🔍 Rechercher un produit..."
                          className="w-full border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500"
                        />
                        
                        {/* Dropdown autocomplétion */}
                        {activeProductDropdown === index && (
                          <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                            {getFilteredProducts(index).length === 0 ? (
                              <div className="px-4 py-3 text-gray-500 text-sm text-center">
                                Aucun produit trouvé
                              </div>
                            ) : (
                              getFilteredProducts(index).map(product => (
                                <button
                                  type="button"
                                  key={product.id}
                                  onClick={() => handleProductSelect(index, product)}
                                  className={`w-full px-4 py-2 text-left hover:bg-indigo-50 flex items-center gap-3 ${
                                    String(item.product_id) === String(product.id) ? 'bg-indigo-50' : ''
                                  }`}
                                >
                                  <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center text-white text-xs font-bold">
                                    {product.nom?.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 truncate text-sm">{product.nom}</p>
                                    <p className="text-xs text-gray-500">
                                      {product.reference && <span className="mr-2">#{product.reference}</span>}
                                      {product.categorie && <span className="text-indigo-600">{product.categorie}</span>}
                                    </p>
                                  </div>
                                  <span className="text-sm font-semibold text-green-600 whitespace-nowrap">
                                    {parseFloat(product.prix_vente).toLocaleString()} XOF
                                  </span>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                        
                        {item.product_id && (
                          <button
                            type="button"
                            onClick={() => {
                              handleItemChange(index, 'product_id', '');
                              setProductSearches(prev => ({ ...prev, [index]: '' }));
                            }}
                            className="absolute right-2 top-7 text-gray-400 hover:text-red-500"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Description *</label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Description de l'article ou service"
                          className="w-full border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500"
                          required
                        />
                      </div>
                    </div>
                    
                    {/* Ligne 2: Quantité, Prix unitaire, Bouton supprimer */}
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Quantité</label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantite}
                          onChange={(e) => handleItemChange(index, 'quantite', e.target.value)}
                          className="w-full border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 text-center"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Prix unitaire (XOF)</label>
                        <input
                          type="number"
                          min="0"
                          value={item.prix_unitaire}
                          onChange={(e) => handleItemChange(index, 'prix_unitaire', e.target.value)}
                          placeholder="0"
                          className="w-full border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500"
                          required
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          disabled={items.length === 1}
                          className="w-full py-2.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          <span>🗑️</span>
                          <span className="hidden md:inline text-sm">Supprimer</span>
                        </button>
                      </div>
                      <div className="hidden md:flex items-end justify-end">
                        <div className="text-right">
                          <span className="text-xs text-gray-500 block">Sous-total</span>
                          <span className="font-semibold text-gray-800">
                            {((parseFloat(item.quantite) || 0) * (parseFloat(item.prix_unitaire) || 0)).toLocaleString()} XOF
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Sous-total mobile */}
                    <div className="md:hidden text-right mt-3 pt-3 border-t border-gray-200">
                      <span className="text-sm font-medium text-gray-600">
                        Sous-total: <span className="text-gray-800">{((parseFloat(item.quantite) || 0) * (parseFloat(item.prix_unitaire) || 0)).toLocaleString()} XOF</span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                type="button"
                onClick={handleAddItem}
                className="mt-4 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-indigo-500 hover:text-indigo-600 transition w-full"
              >
                + Ajouter une ligne
              </button>
            </div>

            {/* Notes */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-800 mb-4">Notes & Conditions</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (visible sur la facture)</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows="2"
                    className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                    placeholder="Notes ou remarques..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Conditions de paiement</label>
                  <textarea
                    value={form.conditions}
                    onChange={(e) => setForm({ ...form, conditions: e.target.value })}
                    rows="2"
                    className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                    placeholder="Ex: Paiement à 30 jours..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow p-6 sticky top-6">
              <h2 className="font-semibold text-gray-800 mb-4">Récapitulatif</h2>
              
              <div className="space-y-3">
                <div className="flex justify-between text-gray-600">
                  <span>Articles ({items.filter(i => i.description).length})</span>
                  <span>{subtotal.toLocaleString()} XOF</span>
                </div>
                
                <div className="border-t pt-3">
                  <div className="flex justify-between text-lg font-bold text-gray-800">
                    <span>Total</span>
                    <span className="text-indigo-600">{subtotal.toLocaleString()} XOF</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 space-y-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : '💾 Enregistrer brouillon'}
                </button>
                
                {id && (
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e, 'send')}
                    disabled={saving}
                    className="w-full py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition disabled:opacity-50"
                  >
                    {saving ? 'Envoi...' : '📤 Enregistrer & Envoyer'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Quick Add Client Modal */}
      {showClientModal && (
        <QuickClientModal 
          onClose={() => setShowClientModal(false)}
          onCreated={(newClient) => {
            setClients([...clients, newClient]);
            setForm({ ...form, client_id: newClient.id });
            setShowClientModal(false);
          }}
          apiBase={apiBase}
        />
      )}
    </div>
  );
};

// Quick Client Modal Component
const QuickClientModal = ({ onClose, onCreated, apiBase = '/business' }) => {
  const [form, setForm] = useState({ nom: '', telephone: '', email: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post(`${apiBase}/clients`, form);
      if (res.data && res.data.id) {
        onCreated(res.data);
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Nouveau client</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
            <input
              type="text"
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
            <input
              type="text"
              value={form.telephone}
              onChange={(e) => setForm({ ...form, telephone: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Création...' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessInvoiceForm;
